/**
 * PDF Viewer Component
 *
 * Renders PDF files using react-pdf with scrollable pages.
 * Handles loading states and errors gracefully.
 * Fetches PDFs with auth headers and uses blob URLs.
 */

import { useCallback, useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Icon, IconKey } from '../icon';

// Configure pdf.js worker from public folder
// This avoids bundler resolution issues with pnpm's non-hoisted structure
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface PdfViewerProps {
  src: string;
  zoom: number;
  onLoadingChange: (loading: boolean) => void;
  onError: (error: string | null) => void;
}

export function PdfViewer({
  src,
  zoom,
  onLoadingChange,
  onError,
}: PdfViewerProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  // Fetch PDF with auth headers and create blob URL
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const fetchPdf = async () => {
      setIsFetching(true);
      setHasError(false);
      setBlobUrl(null);
      setNumPages(null);
      onLoadingChange(true);
      onError(null);

      try {
        const response = await fetch(src, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch PDF');
        }

        const blob = await response.blob();
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setIsFetching(false);
      } catch {
        if (!cancelled) {
          setHasError(true);
          setIsFetching(false);
          onLoadingChange(false);
          onError('Failed to load PDF');
        }
      }
    };

    fetchPdf();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, onLoadingChange, onError]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setHasError(false);
      onLoadingChange(false);
      onError(null);
    },
    [onLoadingChange, onError]
  );

  const onDocumentLoadError = useCallback(
    (error: Error) => {
      console.error('PDF load error:', error);
      setHasError(true);
      setNumPages(null);
      onLoadingChange(false);
      onError('Failed to load PDF');
    },
    [onLoadingChange, onError]
  );

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
        <Icon icon={IconKey.AlertCircle} size={32} />
        <p className="text-sm">Failed to load PDF</p>
      </div>
    );
  }

  if (isFetching || !blobUrl) {
    return (
      <div className="flex items-center justify-center h-full">
        <Icon icon={IconKey.Loading} size={32} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Calculate page width based on zoom
  const baseWidth = 600;
  const scaledWidth = baseWidth * zoom;

  return (
    <div className="w-full h-full overflow-auto bg-muted/20">
      <Document
        file={blobUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={
          <div className="flex items-center justify-center h-full">
            <Icon icon={IconKey.Loading} size={32} className="animate-spin text-muted-foreground" />
          </div>
        }
        className="flex flex-col items-center py-4 gap-4"
      >
        {numPages &&
          Array.from({ length: numPages }, (_, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={scaledWidth}
              className="shadow-md"
              loading={
                <div
                  className="flex items-center justify-center bg-white"
                  style={{ width: scaledWidth, height: scaledWidth * 1.4 }}
                >
                  <Icon icon={IconKey.Loading} size={24} className="animate-spin text-muted-foreground" />
                </div>
              }
            />
          ))}
      </Document>
    </div>
  );
}
